import { Field, TextEditor, validators } from '@approvalmax/ui';
import { constants, selectors } from 'modules/common';
import { FC, memo } from 'react';
import bemFactory from 'react-bem-factory';
import styled from 'styled-components';

import RequestFormRow from '../../../../../components/RequestFormRow/RequestFormRow';
import RequestFormRowGroup from '../../../../../components/RequestFormRowGroup/RequestFormRowGroup';
import RequestFormSection from '../../../../../components/RequestFormSection/RequestFormSection';
import { isValidQBOVendorName } from '../../../../../selectors/qbooks/validationSelectors';
import { useQBOVendorRequestContext } from '../../QBOVendorRequestContext';
import { messages } from './ContactInformationSection.messages';

const { qBooksConstants } = constants;

const qa = bemFactory.qa('reqf-qbooks-vendor-request__information');

interface ContactInformationSectionProps {
    className?: string;
}

const DenseRowField = styled(Field)`
    &:not(:last-child) {
        margin-right: 5px;
    }

    &:not(:first-child) {
        margin-left: 5px;
    }
`;

const checkIsDisplayNameInvalid = (name: string | undefined) => {
    if (!name) {
        return true;
    }

    if (!isValidQBOVendorName(name)) {
        return true;
    }

    const isDisplayNameEmpty = !name.trim();
    const isDisplayNameLengthExceeded = name.length > qBooksConstants.DISPLAY_NAME_MAX_LENGTH;
    const isDisplayNameHasEmoji = /\p{Extended_Pictographic}/u.test(name);

    if (isDisplayNameEmpty || isDisplayNameLengthExceeded || isDisplayNameHasEmoji) {
        return true;
    }

    return false;
};

const ContactInformationSection: FC<ContactInformationSectionProps> = (props) => {
    const { className } = props;

    const { changeVendorProperty, vendor, showErrors } = useQBOVendorRequestContext();

    const isInvalidDisplayName = checkIsDisplayNameInvalid(vendor.displayName);

    const isInvalidFirstName = !!(
        vendor.firstName &&
        (!vendor.firstName?.trim() || vendor.firstName.length > qBooksConstants.FIRST_NAME_MAX_LENGTH)
    );

    const isInvalidCompanyName = !!(
        vendor.companyName &&
        (!vendor.companyName?.trim() || vendor.companyName.length > qBooksConstants.COMPANY_NAME_MAX_LENGTH)
    );

    const isInvalidTitle = !!(
        vendor.title &&
        (!vendor.title?.trim() || vendor.title.length > qBooksConstants.TITLE_MAX_LENGTH)
    );

    const isInvalidMiddleName = !!(
        vendor.middleName &&
        (!vendor.middleName?.trim() || vendor.middleName.length > qBooksConstants.MIDDLE_NAME_MAX_LENGTH)
    );

    const isInvalidLastName = !!(
        vendor.lastName &&
        (!vendor.lastName?.trim() || vendor.lastName.length > qBooksConstants.LAST_NAME_MAX_LENGTH)
    );

    const isInvalidSuffix = !!(
        vendor.suffix &&
        (!vendor.suffix?.trim() || vendor.suffix.length > qBooksConstants.SUFFIX_MAX_LENGTH)
    );

    const isInvalidEmailAddress = !!(
        vendor.emailAddress &&
        (!vendor.emailAddress?.trim() ||
            vendor.emailAddress.length > qBooksConstants.EMAIL_ADDRESS_MAX_LENGTH ||
            !validators.isEmail(vendor.emailAddress))
    );

    const isInvalidWebSite = !!(
        vendor.webSite &&
        (!vendor.webSite?.trim() ||
            vendor.webSite.length > qBooksConstants.WEBSITE_MAX_LENGTH ||
            !selectors.ui.isValidWebsite(vendor.webSite))
    );

    const isInvalidPhone = !!(
        vendor.phone &&
        (!vendor.phone?.trim() ||
            vendor.phone.length > qBooksConstants.PHONE_MAX_LENGTH ||
            !selectors.ui.isValidPhone(vendor.phone))
    );

    const isInvalidMobile = !!(
        vendor.mobile &&
        (!vendor.mobile?.trim() ||
            vendor.mobile.length > qBooksConstants.MOBILE_MAX_LENGTH ||
            !selectors.ui.isValidPhone(vendor.mobile))
    );

    const isInvalidFax = !!(
        vendor.fax &&
        (!vendor.fax?.trim() ||
            vendor.fax.length > qBooksConstants.FAX_MAX_LENGTH ||
            !selectors.ui.isValidPhone(vendor.fax))
    );

    return (
        <RequestFormSection header={messages.contactInformationHeader} className={className}>
            <RequestFormRowGroup>
                <RequestFormRow>
                    <Field title={messages.displayNameAs} required>
                        <TextEditor
                            focusOnMount
                            qa={qa('display-name-as')}
                            value={vendor.displayName}
                            maxLength={qBooksConstants.DISPLAY_NAME_MAX_LENGTH}
                            onChange={changeVendorProperty('displayName')}
                            placeholder={messages.displayNameAsPlaceholder}
                            invalid={showErrors && isInvalidDisplayName}
                        />
                    </Field>

                    <Field title={messages.companyName}>
                        <TextEditor
                            qa={qa('company-name')}
                            value={vendor.companyName || ''}
                            maxLength={qBooksConstants.COMPANY_NAME_MAX_LENGTH}
                            onChange={changeVendorProperty('companyName')}
                            placeholder={messages.companyNamePlaceholder}
                            invalid={showErrors && isInvalidCompanyName}
                        />
                    </Field>
                </RequestFormRow>

                <RequestFormRow>
                    <DenseRowField css='max-width: 50px;' title={messages.title}>
                        <TextEditor
                            qa={qa('title')}
                            value={vendor.title || ''}
                            maxLength={qBooksConstants.TITLE_MAX_LENGTH}
                            onChange={changeVendorProperty('title')}
                            placeholder={messages.titlePlaceholder}
                            invalid={showErrors && isInvalidTitle}
                        />
                    </DenseRowField>

                    <DenseRowField title={messages.givenName}>
                        <TextEditor
                            qa={qa('given-name')}
                            value={vendor.firstName || ''}
                            maxLength={qBooksConstants.FIRST_NAME_MAX_LENGTH}
                            onChange={changeVendorProperty('firstName')}
                            placeholder={messages.givenNamePlaceholder}
                            invalid={showErrors && isInvalidFirstName}
                        />
                    </DenseRowField>

                    <DenseRowField title={messages.middleName}>
                        <TextEditor
                            qa={qa('middle-name')}
                            value={vendor.middleName || ''}
                            maxLength={qBooksConstants.MIDDLE_NAME_MAX_LENGTH}
                            onChange={changeVendorProperty('middleName')}
                            placeholder={messages.middleNamePlaceholder}
                            invalid={showErrors && isInvalidMiddleName}
                        />
                    </DenseRowField>

                    <DenseRowField title={messages.familyName}>
                        <TextEditor
                            qa={qa('family-name')}
                            value={vendor.lastName || ''}
                            maxLength={qBooksConstants.LAST_NAME_MAX_LENGTH}
                            onChange={changeVendorProperty('lastName')}
                            placeholder={messages.familyNamePlaceholder}
                            invalid={showErrors && isInvalidLastName}
                        />
                    </DenseRowField>

                    <DenseRowField css='max-width: 50px;' title={messages.suffix}>
                        <TextEditor
                            qa={qa('suffix')}
                            value={vendor.suffix || ''}
                            maxLength={qBooksConstants.SUFFIX_MAX_LENGTH}
                            onChange={changeVendorProperty('suffix')}
                            placeholder={messages.suffixPlaceholder}
                            invalid={showErrors && isInvalidSuffix}
                        />
                    </DenseRowField>
                </RequestFormRow>

                <RequestFormRow>
                    <Field title={messages.email}>
                        <TextEditor
                            qa={qa('email')}
                            value={vendor.emailAddress || ''}
                            type='email'
                            maxLength={qBooksConstants.EMAIL_ADDRESS_MAX_LENGTH}
                            onChange={changeVendorProperty('emailAddress')}
                            placeholder={messages.emailPlaceholder}
                            invalid={showErrors && isInvalidEmailAddress}
                        />
                    </Field>

                    <Field title={messages.website}>
                        <TextEditor
                            qa={qa('website')}
                            value={vendor.webSite || ''}
                            type='url'
                            maxLength={qBooksConstants.WEBSITE_MAX_LENGTH}
                            onChange={changeVendorProperty('webSite')}
                            placeholder={messages.websitePlaceholder}
                            invalid={showErrors && isInvalidWebSite}
                        />
                    </Field>
                </RequestFormRow>

                <RequestFormRow>
                    <Field title={messages.primaryPhone}>
                        <TextEditor
                            qa={qa('primary-phone')}
                            value={vendor.phone || ''}
                            type='tel'
                            onChange={changeVendorProperty('phone')}
                            maxLength={qBooksConstants.PHONE_MAX_LENGTH}
                            placeholder={messages.primaryPhonePlaceholder}
                            invalid={showErrors && isInvalidPhone}
                        />
                    </Field>

                    <Field title={messages.mobilePhone}>
                        <TextEditor
                            qa={qa('mobile-phone')}
                            value={vendor.mobile || ''}
                            type='tel'
                            maxLength={qBooksConstants.MOBILE_MAX_LENGTH}
                            onChange={changeVendorProperty('mobile')}
                            placeholder={messages.mobilePhonePlaceholder}
                            invalid={showErrors && isInvalidMobile}
                        />
                    </Field>
                </RequestFormRow>

                <RequestFormRow>
                    <Field
                        css='max-width: 50%;
                        min-width: 50%;
                        padding-right: 10px;'
                        title={messages.fax}
                    >
                        <TextEditor
                            qa={qa('fax')}
                            value={vendor.fax || ''}
                            maxLength={qBooksConstants.FAX_MAX_LENGTH}
                            onChange={changeVendorProperty('fax')}
                            placeholder={messages.faxPlaceholder}
                            invalid={showErrors && isInvalidFax}
                        />
                    </Field>
                </RequestFormRow>
            </RequestFormRowGroup>
        </RequestFormSection>
    );
};

export default memo(ContactInformationSection);
